import { DEFAULT_LOCALE, SEO_LOCALES, toCanonicalPath } from 'BomdisoftSeo/config';
import { SeoError } from 'BomdisoftSeo/errors';
import type { SupportedLanguageCode } from '@bomdisoft/translations';

export type HubEnvironment = 'dev' | 'production' | 'staging';

export const HUB_SLUGS = ['clepit', 'devjuju', 'estimojo', 'gamblish', 'njangy', 'pieleaf', 'prodantix', 'workadabra'] as const;

export type HubSlug = (typeof HUB_SLUGS)[number];

const HUB_ORIGIN: Record<HubEnvironment, string> = {
  dev: 'http://localhost:3000',
  production: 'https://www.bomdisoft.com',
  staging: 'https://staging.bomdisoft.com',
};

export type ResolveHubEnvironmentInput = {
  apexOrigin?: string;
  browserHostname?: string;
  nodeEnv?: string;
};

export const resolveHubEnvironment = ({ apexOrigin, browserHostname, nodeEnv }: ResolveHubEnvironmentInput): HubEnvironment => {
  if (nodeEnv === 'development') return 'dev';
  if (browserHostname?.startsWith('staging.') || apexOrigin?.startsWith('https://staging.')) return 'staging';
  return 'production';
};

export type BuildHubUrlInput = {
  environment?: HubEnvironment;
  locale: SupportedLanguageCode;
  slug: HubSlug;
};

export const buildHubUrl = ({ environment = 'production', locale, slug }: BuildHubUrlInput): string => {
  if (!(HUB_SLUGS as readonly string[]).includes(slug)) throw new SeoError('UNKNOWN_HUB_SLUG', `Unknown product hub slug: ${slug}`);

  return `${HUB_ORIGIN[environment]}${toCanonicalPath(locale, `products/${slug}`)}`;
};

export const toSupportedLocale = (value: unknown): SupportedLanguageCode => (typeof value === 'string' && (SEO_LOCALES as readonly string[]).includes(value) ? (value as SupportedLanguageCode) : DEFAULT_LOCALE);

export const hubBacklinkHref = (slug: HubSlug, locale: unknown): string =>
  buildHubUrl({
    environment: resolveHubEnvironment({ apexOrigin: process.env.NEXT_PUBLIC_BOMDISOFT_AUTH_APEX_ORIGIN, nodeEnv: process.env.NODE_ENV }),
    locale: toSupportedLocale(locale),
    slug,
  });
